import { Route } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Routes";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Person } from "@/Common/Models/Person/Person";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { RvqOptions } from "../RvqOptions";
import { ValueSetType } from "./Constants/ValueSetType";
import { QuebecVaccinationRegistryHelper } from "./QuebecVaccinationRegistryHelper";

interface ImmunizationDetailsViewProps {
    immunization: MedicalItemImmunization;
    isQuebecVaccineRegistryActive: boolean;
    rvqOptions: RvqOptions
}

class ImmunizationDetailsViewComponent extends BaseReactComponent<ImmunizationDetailsViewProps, null> {
    public render(): JSX.Element {
        const immunization = this.immunization;

        return (
            <div className="medical-reaction-detail-box" key={"imm" + immunization.Id}>
                <table className="medical-reaction-details border">
                    <tbody>
                        {this.renderRow("ImmunizationNameDLabel", MyleHelper.ReplaceEmptyString(immunization.Name), this.shouldRenderImmunizationNameAndAgent(immunization))}
                        {this.renderRow("QuebecVaccinationRegistry_ImmunizingAgentLabel", this.immunizingAgent, this.shouldRenderImmunizationNameAndAgent(immunization))}
                        {immunization.IsRefused
                            ? this.getVaccineRefusedElements()
                            : this.getVaccineDetailElements()
                        }
                        {this.renderRow("NotesLabel", JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(immunization.Notes)))}
                    </tbody>
                </table>
            </div>
        );
    }

    private shouldRenderImmunizationNameAndAgent(immunization: MedicalItemImmunization): boolean {
        return !this.isRvqForm || !(immunization.IsRefused && immunization.IsQuebecRegistryAllAntigensSelected);
    }

    private get immunizingAgent(): string {
        return (
            this.isRvqForm
                ? QuebecVaccinationRegistryHelper.translateValueSet(
                    this.immunization.QuebecRegistryAgentCode,
                    this.immunization.Type,
                    ValueSetType.Agent
                )
                : this.immunization.Type
        )?.trim() || "--";
    }

    private renderRow(labelKey: string, element: React.ReactNode, renderRow: boolean = true): JSX.Element {
        if (!renderRow)
            return null;

        return (
            <tr key={labelKey}>
                <td className="c1">
                    <span>{cacheStore.GetString(labelKey)}</span>
                </td>
                <td className="c2">
                    <strong>{element}</strong>
                </td>
            </tr>
        );
    }

    private get isRvqForm(): boolean {
        return this.immunization.IsQuebecRegistryForm;
    }

    private get immunization(): MedicalItemImmunization {
        return this.props.immunization;
    }

    private getVaccineRefusedElements(): JSX.Element[] {
        let refusedDateString = this.immunization.RefusedDate != null
            ? PatientMedicalItemsHelper.GetLifeStageDateString(this.immunization.RefusedDate, this.immunization.RefusedPrecisionDate)
            : "--";

        const motiveString = QuebecVaccinationRegistryHelper
            .translateValueSet(
                this.immunization?.QuebecRegistryRefusalMotiveCode,
                this.props.rvqOptions.vaccinationRefusalMotives?.[this.immunization?.QuebecRegistryRefusalMotiveCode]?.Label ?? "--",
                ValueSetType.VaccinationRefusalMotive
            );

        let antigen = this.immunization.IsQuebecRegistryAllAntigensSelected
            ? cacheStore.GetString("QuebecVaccinationRegistry_AllAntigensLabel")
            : this.getAntigenString();

        return [
            this.renderRow("QuebecVaccinationRegistry_AntigenLabel", MyleHelper.ReplaceEmptyString(antigen), this.isRvqForm),
            this.renderRow("RefusedDateDLabel", refusedDateString),
            this.renderRow("QuebecVaccinationRegistry_MotiveLabel", motiveString, this.isRvqForm)
        ];
    }

    private getVaccineDetailElements(): JSX.Element[] {
        const immunization = this.immunization;
        const vaccinatorName = this.isRvqForm || !immunization?.VaccinatorEmployeeId
            ? immunization?.AdministeredByDisplayText
            : Person.FromEmployee(cacheStore.GetEmployeeByContactFacetId(immunization.VaccinatorEmployeeId))?.NameWithIdentifier;

        return [
            this.renderRow("QuebecVaccinationRegistry_AdministrationReasonLabel", this.getAdministrationReasonText(), this.isRvqForm && !!immunization.QuebecRegistryAdministeredReasonCode),
            this.renderRow("AdministeredDateDLabel", this.getAdministeredDateString()),
            this.renderRow(
                "QuebecVaccinationRegistry_ImmunizationLocationLabel",
                immunization.QuebecRegistryLocationId && immunization?.LocationDisplayText
                    ? immunization?.LocationDisplayText
                    : "--",
                this.isRvqForm
            ),
            this.renderRow(
                "VaccinatorLabel",
                vaccinatorName || "--"
            ),
            this.renderRow("BoosterLabel", MyleHelper.ReplaceEmptyString(immunization?.BoosterInMonth?.toString() ?? "")),
            this.renderRow("ManufacturerDLabel", MyleHelper.ReplaceEmptyString(immunization.Manufacturer)),
            this.renderRow("LotNumberDLabel", MyleHelper.ReplaceEmptyString(immunization.LotNumber)),
            this.renderRow("lblDSQPosologyRoute", this.getRouteString()),
            this.renderRow("QuebecVaccinationRegistry_AdministrationSiteLabel", this.getSiteString()),
            this.renderRow("QuebecVaccinationRegistry_QuantityAdministeredLabel", this.getDoseString()),
            this.renderRow("InstructionsLabel", JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(immunization.Instruction)))
        ];
    }

    private getAdministrationReasonText(): React.ReactNode {
        const defaultValue = this.props.rvqOptions.administrationReasons?.[this.immunization.QuebecRegistryAdministeredReasonCode]?.Label ?? "--";
        return QuebecVaccinationRegistryHelper.translateValueSet(
            this.immunization.QuebecRegistryAdministeredReasonCode,
            defaultValue,
            ValueSetType.AdministrationReason
        );
    }

    private getSiteString(): React.ReactNode {
        let siteLabel = "--";
        if (!this.isRvqForm)
            return cacheStore.GetLocalizedValueByLookupId(this.immunization.SiteLookupId) || siteLabel;

        siteLabel = this.props.rvqOptions.administrationSites?.[this.immunization.QuebecRegistrySiteCode]?.Label || siteLabel;
        return QuebecVaccinationRegistryHelper.translateValueSet(this.immunization.QuebecRegistrySiteCode, siteLabel, ValueSetType.AdministrationSite);
    }

    private getDoseString(): React.ReactNode {
        let unitLabel = "--";
        if (!this.isRvqForm)
            unitLabel = cacheStore.GetLocalizedValueByLookupId(this.immunization.DoseUnitLookupId)?.trim() || unitLabel;
        else {
            unitLabel = this.props.rvqOptions.dosageUnits?.[this.immunization.QuebecRegistryDoseUnitCode]?.Label || unitLabel;
            unitLabel = QuebecVaccinationRegistryHelper.translateValueSet(this.immunization.QuebecRegistryDoseUnitCode, unitLabel, ValueSetType.DosageUnit);
        }

        const doseLabel = this.immunization.Dose?.toString().trim() ?? "--";
        return `${doseLabel} ${unitLabel}`;
    }

    private getRouteString(): string {
        let routeLabel = "--";
        if (this.isRvqForm) {
            routeLabel = this.props.rvqOptions.routes?.[this.immunization.RouteCode]?.Label || routeLabel;
            routeLabel = QuebecVaccinationRegistryHelper.translateValueSet(this.immunization.RouteCode, routeLabel, ValueSetType.AdministrationRoute);
        }
        else {
            const route = cacheStore.RoutesCache.firstOrDefault(x => x.Code == this.immunization.RouteCode);
            if (route)
                routeLabel = Route.GetLocalizedDescription(route);
        }
        return routeLabel;
    }

    private getAdministeredDateString(): string {
        return this.immunization.AdministeredDate != null
            ? PatientMedicalItemsHelper.GetLifeStageDateString(this.immunization.AdministeredDate, this.immunization.AdministeredPrecisionDate)
            : "--";
    }

    private getAntigenString(): string {
        if (!this.immunization.QuebecRegistryAntigenCodes)
            return "--";

        let antigens = this.immunization.QuebecRegistryAntigenCodes.split(",").map(code => code?.trim());
        let antigenString = "--";

        if (cacheStore.ClinicSettings.EnableQuebecVaccinationRegistry) {
            const translatedAntigens = QuebecVaccinationRegistryHelper.getTranslatedDictionary(
                this.props.rvqOptions?.antigens,
                ValueSetType.Antigen
            );

            const antigenCodeValues = QuebecVaccinationRegistryHelper.filterCodeValues(QuebecVaccinationRegistryHelper.getCodeValues(KeyValuePair.fromMapOfStrings(translatedAntigens)), antigens);

            if (antigenCodeValues?.any())
                antigenString = antigenCodeValues
                    .map(x => x.Label)
                    .join(", ");
        }
        else {
            antigenString = antigens
                // Default to the actual code in case the RVQ adds new antigens that aren't in our vocab files.
                .map(code => QuebecVaccinationRegistryHelper.translateValueSet(code, code, ValueSetType.Antigen))
                .filter(label => !!label)
                .join(", ");
        }

        return antigenString;
    }
}

export { ImmunizationDetailsViewComponent };
